import React from 'react';
import FormHelperText from '@mui/material/FormHelperText';
import OutlinedInput from '@mui/material/OutlinedInput';
import Typography from '@mui/material/Typography';
import { gray100, gray40, gray60, gray80, red50 } from '../../constants/colors';
import { formLabelWrapper, formNarrowMb, formWideMb } from '../../constants/location';
import type { SxProps } from '@mui/material';
import type { FieldError, FieldErrorsImpl, Merge } from 'react-hook-form';

type OutlinedInputProps = React.ComponentProps<typeof OutlinedInput>;
type Props = OutlinedInputProps & {
  labelText?: string;
  requiredText?: string;
  isRequired: boolean;
  error?: boolean;
  helperText?: string | FieldError | Merge<FieldError, FieldErrorsImpl> | undefined;
  formMb?: string | undefined;
  guideText?: string;
  underText?: string;
  otherError?: string | null;
  heightIs40px?: boolean;
  paddingIs10px8px?: boolean;
  fontSizeIs14px?: boolean;
  backgroundColor?: string;
};

const CustomNumberField = React.forwardRef(
  (props: Props, ref: React.Ref<HTMLInputElement>): React.ReactElement => {
    const {
      labelText,
      requiredText,
      isRequired,
      error,
      helperText,
      formMb,
      heightIs40px,
      paddingIs10px8px,
      fontSizeIs14px,
      backgroundColor,
      ...rest
    } = props;

    const styles = {
      formLabelWrapper,
      helper: {
        '@media screen and (max-width:480px)': {
          marginLeft: 0,
        },
      },
      input: {
        '&.MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
          borderColor: error ? red50 : gray60,
          borderWidth: '1px',
          backgroundColor: backgroundColor ?? 'transparent',
        },
        '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: error ? red50 : gray100,
          borderWidth: '1px',
        },
        '&.MuiOutlinedInput-root:hover': {
          borderColor: error ? red50 : gray100,
          borderWidth: '1px',
        },
        '@media screen and (max-width:480px)': {
          width: '100%',
        },
        '& .MuiOutlinedInput-input': {
          padding: paddingIs10px8px ? '10px 8px' : '16.5px 14px',
          fontSize: fontSizeIs14px ? '14px' : '16px',
        },
        width: '100%',
        height: heightIs40px ? '41px' : '51px',
      },
      label: {
        fontSize: '12px',
      },
      required: {
        color: red50,
        fontSize: '12px',
        marginLeft: '10px',
      },
      underText: {
        color: gray40,
        ml: '14px',
      },
    };

    const inputformMb = (marginBottom: string | undefined): SxProps =>
      marginBottom === 'narrow' ? formNarrowMb : formWideMb;

    const textfieldHelper = (): string | undefined => {
      if (helperText === undefined) return;
      return String(helperText);
    };

    return (
      <Typography sx={inputformMb(formMb)} align="left" component="div">
        <Typography align="left" sx={styles.formLabelWrapper} component="div">
          <Typography color={gray80} sx={styles.label} component="span">
            {labelText}
          </Typography>
          {isRequired ? (
            <Typography component="span" sx={styles.required}>
              {requiredText}
            </Typography>
          ) : (
            ''
          )}
        </Typography>
        <OutlinedInput {...rest} ref={ref} value={rest.value ?? ''} sx={styles.input} />
        <FormHelperText sx={styles.helper} error={error}>
          {error && textfieldHelper()}
        </FormHelperText>
      </Typography>
    );
  },
);

CustomNumberField.displayName = 'CustomNumberField';
export default CustomNumberField;
